import type React from 'react';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogTitle } from '../tailgrids/core/dialog.js';
import { Backdrop } from '../tailgrids/core/overlay.js';
import { cn } from '../../utils/cn.js';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  titleIcon?: React.ReactNode;
  /** One line under the title saying what the dialog is for. */
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** `full` fills the window less a 1 rem margin, at a fixed height (an expanded chart). */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Escape and an outside click close the dialog (default). Turn off while a submit is running. */
  dismissable?: boolean;
}

const WIDTH = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  full: 'h-[calc(100dvh-2rem)] max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)]',
};

/** The layer every overlay that blocks the page shares (the vendored sheet's overlay is z-9999). */
export const MODAL_LAYER = 'z-9999';

/**
 * A modal dialog (NextAdmin dialog on react-aria): focus is trapped and restored, Escape and the backdrop close it,
 * the page behind does not scroll. For a short yes/no question use `ConfirmDialog`; for a side panel use `Sheet`.
 * Never build an overlay with `fixed inset-0` by hand.
 */
export function Modal({
  open,
  onClose,
  title,
  titleIcon,
  description,
  children,
  footer,
  maxWidth = 'md',
  dismissable = true,
}: ModalProps) {
  return (
    <Backdrop
      isOpen={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      isDismissable={dismissable}
      // The sheet layer (`SheetOverlay` is z-9999): overlays at one layer stack in the order they open, so a dialog
      // opened from a side sheet (Members → Add member) sits on top of it instead of under its backdrop.
      className={MODAL_LAYER}
      isKeyboardDismissDisabled={!dismissable}
    >
      <Dialog
        aria-label={title ? undefined : 'Dialog'}
        showCloseButton={dismissable}
        className={cn('flex max-h-[85vh] flex-col p-0', WIDTH[maxWidth])}
      >
        {title && (
          <DialogHeader className="shrink-0 gap-1 border-b border-card-border py-4 pr-14 pl-5">
            <DialogTitle className="flex items-center gap-2 text-lg leading-7">
              {titleIcon}
              {title}
            </DialogTitle>
            {description && <p className="text-sm text-text-100">{description}</p>}
          </DialogHeader>
        )}
        <DialogBody className="min-h-0 flex-1 overflow-auto px-5 py-4">{children}</DialogBody>
        {footer && <DialogFooter className="shrink-0 border-t border-card-border px-5 py-4">{footer}</DialogFooter>}
      </Dialog>
    </Backdrop>
  );
}
